import { useNavigate, useLocation } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Plus, LayoutGrid, List, Package, Sparkles, Radio, FileEdit } from "lucide-react";
import { BackLink } from "@/components/layout/BackLink";
import { cn } from "@/lib/utils";
import { OFFERING_TYPE_THEME } from "@/lib/offeringTypeTheme";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "@/hooks/use-auth-compat";
import { toast } from "@/hooks/use-toast";
import { TeacherOfferingCard, type Offering, type OfferingStatus } from "@/components/teacher/dashboard/TeacherOfferingCard";
import { useTeacherProfile } from "@/contexts/TeacherProfileContext";
import { getOfferings } from "@/services/OfferingServices";
import { GracePeriodBanner } from "@/components/teacher/GracePeriodBanner";
import { AdBoostPurchaseCard } from "@/components/teacher/AdBoostPurchaseCard";

const typeMap: Record<string, Offering["type"]> = {
  ONE_TO_ONE: "1-on-1 Coach",
  GROUP: "Group Coaching",
  CRASH_COURSE: "Crash Course",
  COURSE: "Specialized Program",
};

function mapApiOffering(o: any): Offering {
  const offeringType = o.offeringType || o.type;
  const isGroup = offeringType === "GROUP";
  const rawStatus = (o.overallStatus || o.status || "DRAFT") as OfferingStatus;
  return {
    id: o.id,
    title: o.title || o.commercialName || o.subjectName || (isGroup ? "Group Offering" : "1-on-1 Offering"),
    type: typeMap[offeringType] ?? (isGroup ? "Group Coaching" : "1-on-1 Coach"),
    subject: o.subject?.name || o.subjectName || "",
    topics: o.topicNames || [],
    pricingPlans: o.pricingPlans ?? [],
    duration: o.oneToOne?.sessionDurationMinutes ? `${o.oneToOne.sessionDurationMinutes} min`
      : o.group?.sessionDurationMinutes ? `${o.group.sessionDurationMinutes} min`
      : o.crashCourse?.sessionDurationMinutes ? `${o.crashCourse.sessionDurationMinutes} min` : "",
    description: o.description || "",
    status: rawStatus,
    active: rawStatus === "PUBLISHED",
    // extended
    teachingMode: o.teachingMode || undefined,
    gradeBands: o.gradeBands?.length ? o.gradeBands : undefined,
    curriculumSystems: o.curriculumSystems?.length ? o.curriculumSystems : undefined,
    trialSessionAvailable: o.trialSessionAvailable ?? false,
    learningOutcomes: o.learningOutcomes || undefined,
    prerequisites: o.prerequisites || undefined,
    targetScorePromise: o.targetScorePromise || undefined,
    earlyBirdDeadline: o.earlyBirdDeadline || undefined,
    // group
    sessionsPerWeek: o.group?.sessionsPerWeek || undefined,
    totalSessionsPlanned: o.group?.totalSessionsPlanned || undefined,
    nextBatchStartDate: o.group?.nextBatchStartDate || undefined,
    batchSizeMin: o.group?.batchSizeMin ?? undefined,
    batchSizeMax: o.group?.batchSizeMax ?? undefined,
    seatsRemaining: o.computedFields?.seatsRemaining ?? undefined,
    currentEnrollment: o.computedFields?.currentEnrollment ?? undefined,
    // crash course
    courseStartDate: o.crashCourse?.courseStartDate || undefined,
    courseEndDate: o.crashCourse?.courseEndDate || undefined,
    totalSessions: o.crashCourse?.totalSessions || undefined,
    seatsTotal: o.crashCourse?.seatsTotal ?? undefined,
    studyMaterialIncluded: o.crashCourse?.studyMaterialIncluded ?? undefined,
    mockTestsIncluded: o.crashCourse?.mockTestsIncluded || undefined,
  };
}

export default function TeacherOfferingsPage() {
  usePageTitle('My Offerings | BookMyCoaching');
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { teacherProfile } = useTeacherProfile();
  const teacherName = teacherProfile?.fullName
    || (teacherProfile?.fullName
      ? `${teacherProfile.fullName}`
      : teacherProfile?.fullName  || "");
  const teacherAvatar = teacherProfile?.profileImage || teacherProfile?.profileImage  || "";
  const teacherSubjects: string[] = (teacherProfile?.subjects || []).map((s: any) =>
    typeof s === "string" ? s : s?.subjectName || s?.subject?.name || s?.name || ""
  ).filter(Boolean);
  const [rawOfferings, setRawOfferings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const lastTokenRef = useRef<string | null>(null);

  useEffect(() => {
    (async () => {
      const token = await auth.getToken();
      if (!token || token === lastTokenRef.current) return;
      lastTokenRef.current = token;
      setLoading(true);
      getOfferings(token)
        .then(data => setRawOfferings(Array.isArray(data) ? data : data?.offerings ?? []))
        .catch(err => {
          console.error('Failed to fetch offerings', err);
          toast({ title: "Failed to load offerings", description: err.message, variant: "destructive" });
        })
        .finally(() => setLoading(false));
    })();
  }, [auth.user?.access_token]);

  const offerings = useMemo(() => rawOfferings.map(o => mapApiOffering(o)), [rawOfferings]);

  const [toggleId, setToggleId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [activeTab, setActiveTab] = useState((location.state as { activeTab?: string } | null)?.activeTab || "all");

  const filterByType = (type?: string) =>
    type ? offerings.filter((o) => o.type === type) : offerings;

  const stats = {
    total: offerings.length,
    active: offerings.filter((o) => o.active).length,
  };

  const handleEdit = (id: string) => navigate(`/teacher-offering/${id}`, { state: { fromTab: activeTab } });

  const handleToggleActive = (id: string) => setToggleId(id);
  const confirmToggle = () => {
    if (toggleId) {
      const offering = offerings.find((o) => o.id === toggleId);
      setRawOfferings(rawOfferings.map((o) => o.id === toggleId ? { ...o, status: offering?.active ? 'UNPUBLISHED' : 'PUBLISHED' } : o));
      toast({
        title: offering?.active ? "Offering deactivated" : "Offering activated",
        description: offering?.active ? "Students can no longer see this offering." : "This offering is now visible to students.",
      });
      setToggleId(null);
    }
  };

  const toggleOffering = offerings.find((o) => o.id === toggleId);
  const cardProps = { onEdit: handleEdit, onToggleActive: handleToggleActive };

  const skeletonGrid = (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {[1, 2, 3].map(i => (
        <div key={i} className="rounded-xl border bg-card p-4 space-y-3 animate-pulse">
          <div className="h-4 bg-muted rounded w-1/3" />
          <div className="h-5 bg-muted rounded w-2/3" />
          <div className="h-3 bg-muted rounded w-full" />
          <div className="h-3 bg-muted rounded w-4/5" />
        </div>
      ))}
    </div>
  );

  const emptyState = (
    <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
      <Package className="w-12 h-12 text-muted-foreground/30 mb-3" />
      <p className="text-sm text-muted-foreground">No offerings in this category yet.</p>
      <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={() => navigate("/create-offering")}>
        <Plus className="w-3.5 h-3.5" /> Create One
      </Button>
    </div>
  );

  const backButton = <BackLink label="Back to My Offerings" onClick={() => setActiveTab("all")} className="mb-4" />;

  const gridClass = viewMode === "grid"
    ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch"
    : "flex flex-col gap-4";

  const renderGrid = (items: Offering[]) => {
    if (loading) return skeletonGrid;
    return (
      <div className={gridClass}>
        {items.length > 0 ? items.map((o) => (
          <TeacherOfferingCard key={o.id} offering={o} {...cardProps} />
        )) : emptyState}
      </div>
    );
  };

  // Grouped view: Live section then Draft section
  const renderGrouped = (items: Offering[]) => {
    if (loading) return skeletonGrid;
    const live = items.filter(o => o.status === "PUBLISHED");
    const drafts = items.filter(o => o.status !== "PUBLISHED");
    if (items.length === 0) return <div className={gridClass}>{emptyState}</div>;
    const showHeaders = false;
    return (
      <div className="space-y-8">
        {live.length > 0 && (
          <div className="space-y-4">
            {showHeaders && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                  <Radio className="w-3 h-3 animate-pulse" />
                  Live — {live.length}
                </div>
                <div className="flex-1 h-px bg-emerald-100" />
              </div>
            )}
            <div className={gridClass}>
              {live.map(o => <TeacherOfferingCard key={o.id} offering={o} {...cardProps} />)}
            </div>
          </div>
        )}
        {drafts.length > 0 && (
          <div className="space-y-4">
            {showHeaders && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                  <FileEdit className="w-3 h-3" />
                  Drafts — {drafts.length}
                </div>
                <div className="flex-1 h-px bg-amber-100" />
              </div>
            )}
            <div className={gridClass}>
              {drafts.map(o => <TeacherOfferingCard key={o.id} offering={o} {...cardProps} />)}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white teacher-theme">
      <SidebarProvider>
        <DashboardTopNav role="teacher" />
        <div className="min-h-screen flex w-full pt-14">
          <TeacherSidebar
            teacherName={teacherName}
            teacherAvatar={teacherAvatar}
            subjects={teacherSubjects}
          />

          <div className="flex-1 flex flex-col min-w-0">

            <main className="flex-1 overflow-auto bg-slate-50">

              <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-slate-900">My Offerings</h1>
                    <p className="text-xs sm:text-sm text-slate-500 mt-0.5">1-on-1, group classes, and crash courses</p>
                  </div>
                  <Button className="gap-1.5 shrink-0 h-9 sm:h-10 active:scale-95 transition-transform" onClick={() => navigate("/create-offering")}>
                    <Plus className="w-4 h-4" /> Create
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 text-center bg-white rounded-xl px-3 py-2 shadow-sm border border-slate-100">
                    <p className="text-base font-bold text-slate-800 leading-none">{stats.total}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Total</p>
                  </div>
                  <div className="flex-1 text-center bg-white rounded-xl px-3 py-2 shadow-sm border border-emerald-100">
                    <p className="text-base font-bold text-emerald-600 leading-none">{stats.active}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Live</p>
                  </div>
                  <div className="flex-1 text-center bg-white rounded-xl px-3 py-2 shadow-sm border border-amber-100">
                    <p className="text-base font-bold text-amber-500 leading-none">{stats.total - stats.active}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Draft</p>
                  </div>
                </div>
              </div>
              <div className="px-4 sm:px-6 py-5 space-y-5">
                <GracePeriodBanner />
                {/* Tabs + View Toggle */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
                  {/* Tab bar + view toggle — horizontally scrollable on mobile */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 overflow-x-auto no-scrollbar">
                      <TabsList className="h-auto bg-white border border-slate-200 rounded-xl p-1 gap-1 shadow-sm inline-flex min-w-max">
                        <TabsTrigger
                          value="all"
                          className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=inactive]:text-slate-500 data-[state=inactive]:hover:text-slate-800 active:scale-95 transition-all"
                        >
                          <Package className="w-4 h-4" />
                          All
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 data-[state=active]:bg-white/20 data-[state=active]:text-white ml-0.5">
                            {stats.total}
                          </span>
                        </TabsTrigger>
                        <TabsTrigger
                          value="1-1"
                          className={cn(
                            "flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold data-[state=inactive]:text-slate-500 data-[state=inactive]:hover:text-slate-800 active:scale-95 transition-all",
                            OFFERING_TYPE_THEME.ONE_TO_ONE.tabActive,
                          )}
                        >
                          <OFFERING_TYPE_THEME.ONE_TO_ONE.icon className="w-4 h-4" />
                          1-on-1
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 data-[state=active]:bg-white/20 data-[state=active]:text-white ml-0.5">
                            {offerings.filter(o => o.type === "1-on-1 Coach").length}
                          </span>
                        </TabsTrigger>
                        <TabsTrigger
                          value="group"
                          className={cn(
                            "flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold data-[state=inactive]:text-slate-500 data-[state=inactive]:hover:text-slate-800 active:scale-95 transition-all",
                            OFFERING_TYPE_THEME.GROUP.tabActive,
                          )}
                        >
                          <OFFERING_TYPE_THEME.GROUP.icon className="w-4 h-4" />
                          Group
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 data-[state=active]:bg-white/20 data-[state=active]:text-white ml-0.5">
                            {offerings.filter(o => o.type === "Group Coaching").length}
                          </span>
                        </TabsTrigger>
                        <TabsTrigger
                          value="crash"
                          className={cn(
                            "flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold data-[state=inactive]:text-slate-500 data-[state=inactive]:hover:text-slate-800 active:scale-95 transition-all",
                            OFFERING_TYPE_THEME.CRASH_COURSE.tabActive,
                          )}
                        >
                          <OFFERING_TYPE_THEME.CRASH_COURSE.icon className="w-4 h-4" />
                          Crash Course
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 data-[state=active]:bg-white/20 data-[state=active]:text-white ml-0.5">
                            {offerings.filter(o => o.type === "Crash Course").length}
                          </span>
                        </TabsTrigger>
                      </TabsList>
                    </div>
                    {/* View toggle — visible on all screen sizes */}
                    <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm shrink-0">
                      <button
                        onClick={() => setViewMode("grid")}
                        className={`p-2.5 active:scale-95 transition-all ${viewMode === "grid" ? "bg-slate-100 text-slate-800" : "text-slate-400 hover:text-slate-700"}`}
                      >
                        <LayoutGrid className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setViewMode("list")}
                        className={`p-2.5 active:scale-95 transition-all ${viewMode === "list" ? "bg-slate-100 text-slate-800" : "text-slate-400 hover:text-slate-700"}`}
                      >
                        <List className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <TabsContent value="all">{renderGrouped(filterByType())}</TabsContent>
                  <TabsContent value="1-1">{backButton}{renderGrouped(filterByType("1-on-1 Coach"))}</TabsContent>
                  <TabsContent value="group">{backButton}{renderGrouped(filterByType("Group Coaching"))}</TabsContent>
                  <TabsContent value="crash">{backButton}{renderGrouped(filterByType("Crash Course"))}</TabsContent>
                </Tabs>

                {/* Boost your offerings — page-level, never attached to an individual offering.
                    One purchase lifts ALL of this teacher's offerings: boost_score is a single
                    value on teacher_profiles (ADR-158), so a per-offering CTA would misrepresent
                    what is being bought.

                    Mounted OUTSIDE <Tabs> deliberately. Radix unmounts inactive TabsContent, so
                    placing this inside would create four independent instances -- one per tab --
                    each running its own useAdBoostStatus/useAdBoostPackages and re-mounting on
                    every tab switch. Out here it mounts once, shows on every tab, and renders for
                    a teacher with no offerings without needing a special case.

                    The card owns its own loading / eligible / active-boost / not-eligible states;
                    nothing is re-implemented at page level. */}
                <AdBoostPurchaseCard />
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>

      {/* Toggle Active Confirmation Dialog */}
      <AlertDialog open={!!toggleId} onOpenChange={(open) => !open && setToggleId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {toggleOffering?.active ? "Deactivate" : "Activate"} Offering
            </AlertDialogTitle>
            <AlertDialogDescription>
              {toggleOffering?.active
                ? "Deactivating this offering will hide it from students. You can reactivate it later."
                : "Activating this offering will make it visible to students again."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmToggle}>
              {toggleOffering?.active ? "Deactivate" : "Activate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
